import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "react-toastify";
import {
  useAdminContent,
  useDeleteContentRecord,
  useSaveContentRecord,
} from "../../api/admin";
import { AdminContentRecord, AdminContentResource } from "../../types/admin";
import { notify } from "./notify";
import { DEFAULT_PAGE, paginate } from "./pagination";
import {
  Button,
  Card,
  Empty,
  Loading,
  PageHeader,
  TextArea,
  PaginationBar,
} from "./ui";
import { SEARCH_INPUT_PROPS } from "./inputs";

const RESOURCES: Record<
  AdminContentResource,
  { label: string; template: Record<string, unknown> }
> = {
  chapters: {
    label: "Chapitres",
    template: { number: null, content: { fr: "", en: "" } },
  },
  questions: {
    label: "Questions",
    template: { number: null, chapter_id: null, content: { fr: "", en: "" } },
  },
  languages: {
    label: "Langues",
    template: {
      name: "",
      local: "",
      country_code: "",
      default: false,
      reading_direction: "ltr",
    },
  },
  narration_styles: {
    label: "Styles de narration",
    template: {
      published: true,
      name: { fr: "" },
      description: { fr: "" },
      tone: { fr: "" },
      example: { fr: "" },
    },
  },
};

const READ_ONLY = ["id", "created_at", "updated_at"];

const editable = (record: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(record).filter(([key]) => !READ_ONLY.includes(key)),
  );

/** Short French (or first available) label for translated JSON fields. */
const preview = (value: unknown) => {
  if (value && typeof value === "object") {
    const translations = value as Record<string, unknown>;
    return String(translations.fr ?? Object.values(translations)[0] ?? "");
  }
  return String(value ?? "");
};

export const AdminContent = () => {
  const [resource, setResource] = useState<AdminContentResource>("chapters");
  const { data, isLoading } = useAdminContent(resource);
  const [creating, setCreating] = useState(false);
  const [filter, setFilterState] = useState("");
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [chapterId, setChapterIdState] = useState("");
  // Questions are listed under their chapter
  const { data: chaptersData } = useAdminContent("chapters");
  const chapters = new Map(
    (chaptersData?.records ?? []).map((chapter) => [chapter.id, chapter]),
  );
  const isQuestions = resource === "questions";

  // The reference content is small: it is filtered and paginated client-side
  const setFilter = (value: string) => {
    setFilterState(value);
    setPage((p) => ({ ...p, page: 1 }));
  };
  const setChapterId = (value: string) => {
    setChapterIdState(value);
    setPage((p) => ({ ...p, page: 1 }));
  };

  const filtered = (data?.records ?? []).filter(
    (record) =>
      JSON.stringify(record).toLowerCase().includes(filter.toLowerCase()) &&
      (!isQuestions || !chapterId || String(record.chapter_id) === chapterId),
  );
  const { items: records, pagination } = paginate(filtered, page);
  const chapterTitle = (id: unknown) => {
    const chapter = chapters.get(Number(id));
    return chapter
      ? `Chapitre ${String(chapter.number)} · ${preview(chapter.content)}`
      : `Chapitre #${String(id)}`;
  };

  return (
    <>
      <PageHeader
        title="Contenu"
        subtitle="Référentiel partagé par tous les livres. Les champs traduits sont des objets { langue: texte }."
        action={
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus className="w-4 h-4" /> Ajouter
          </Button>
        }
      />

      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex gap-1">
          {(Object.keys(RESOURCES) as AdminContentResource[]).map((value) => (
            <button
              key={value}
              onClick={() => {
                setResource(value);
                setPage((p) => ({ ...p, page: 1 }));
                setCreating(false);
              }}
              className={`px-4 py-1.5 text-sm font-medium rounded-md ${
                resource === value
                  ? "bg-indigo-600 text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {RESOURCES[value].label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {isQuestions && (
            <select
              value={chapterId}
              onChange={(e) => setChapterId(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm max-w-xs"
            >
              <option value="">Tous les chapitres</option>
              {[...chapters.values()].map((chapter) => (
                <option key={chapter.id} value={chapter.id}>
                  {chapterTitle(chapter.id)}
                </option>
              ))}
            </select>
          )}
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            {...SEARCH_INPUT_PROPS}
            placeholder="Filtrer…"
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-60"
          />
        </div>
      </div>

      {creating && (
        <div className="mb-4">
          <RecordEditor
            resource={resource}
            initial={
              isQuestions && chapterId
                ? {
                    ...RESOURCES[resource].template,
                    chapter_id: Number(chapterId),
                  }
                : RESOURCES[resource].template
            }
            onDone={() => setCreating(false)}
          />
        </div>
      )}

      {isLoading ? (
        <Loading />
      ) : records.length === 0 ? (
        <Card>
          <Empty>Aucun élément.</Empty>
        </Card>
      ) : (
        <div className="space-y-2">
          {records.map((record, index) => (
            <div key={record.id}>
              {isQuestions &&
                (index === 0 ||
                  records[index - 1].chapter_id !== record.chapter_id) && (
                  <h3 className="text-sm font-semibold text-gray-700 mt-4 mb-2">
                    {chapterTitle(record.chapter_id)}
                  </h3>
                )}
              <RecordRow resource={resource} record={record} />
            </div>
          ))}
          <Card>
            <PaginationBar pagination={pagination} onChange={setPage} />
          </Card>
        </div>
      )}
    </>
  );
};

const RecordRow = ({
  resource,
  record,
}: {
  resource: AdminContentResource;
  record: AdminContentRecord;
}) => {
  const [editing, setEditing] = useState(false);
  const remove = useDeleteContentRecord(resource);
  const title = preview(record.content ?? record.name);

  if (editing) {
    return (
      <RecordEditor
        resource={resource}
        id={record.id}
        initial={editable(record)}
        onDone={() => setEditing(false)}
      />
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between px-5 py-3 gap-4">
        <div className="min-w-0 text-sm">
          <span className="text-gray-400 mr-2">#{record.id}</span>
          {record.number != null && (
            <span className="font-medium text-gray-700 mr-2">
              {String(record.number)}.
            </span>
          )}
          <span className="text-gray-900">{title}</span>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" onClick={() => setEditing(true)}>
            Modifier
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => remove.mutate(record.id, notify("Supprimé"))}
            disabled={remove.isLoading}
            confirm={`Supprimer « ${title} » ? Les livres qui l'utilisent peuvent casser.`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

const RecordEditor = ({
  resource,
  id,
  initial,
  onDone,
}: {
  resource: AdminContentResource;
  id?: number;
  initial: Record<string, unknown>;
  onDone: () => void;
}) => {
  const [json, setJson] = useState(JSON.stringify(initial, null, 2));
  const save = useSaveContentRecord(resource);

  const submit = () => {
    let record: Record<string, unknown>;
    try {
      record = JSON.parse(json);
    } catch {
      toast.error("JSON invalide");
      return;
    }
    save.mutate({ id, record }, notify("Enregistré", onDone));
  };

  return (
    <Card title={id ? `Modifier #${id}` : "Nouvel élément"}>
      <div className="p-5 space-y-3">
        <TextArea
          mono
          rows={Math.min(30, json.split("\n").length + 2)}
          value={json}
          onChange={setJson}
        />
        <div className="flex gap-2">
          <Button variant="primary" onClick={submit} disabled={save.isLoading}>
            Enregistrer
          </Button>
          <Button onClick={onDone}>Annuler</Button>
        </div>
      </div>
    </Card>
  );
};
